import { z } from 'zod';
import { itemTipo } from './itens.js';
import { cuid, dinheiro, isoDate, taxa } from './primitives.js';

/**
 * Sessao 11, item 3: liberacao de preco. O praticado e editado num RASCUNHO e so vale depois de PUBLICADO (append-only, com vigencia).
 * Na condicao BASE da tabela o preco e digitado; as demais condicoes DERIVAM da base pelo fator financeiro (motor:
 * praticadoDeCondicao). Um override por condicao fica marcado `manual`. Permissao: praticado.gravar (FINANCEIRO, ADMIN_*, CONSULTOR).
 */

const precoPositivo = dinheiro.refine((v) => /[1-9]/.test(v), 'O preco deve ser maior que zero');

export const POLITICAS_PRECO = ['NEGOCIADO', 'TABELA'] as const;
export const politicaPrecoSchema = z.enum(POLITICAS_PRECO);
export type PoliticaPreco = z.infer<typeof politicaPrecoSchema>;
export interface PoliticaPrecoResponse {
  politicaPreco: PoliticaPreco;
}

export const ORIGENS_PRATICADO = ['BASE', 'DERIVADO', 'MANUAL', 'DIRETO'] as const;
export type OrigemPraticado = (typeof ORIGENS_PRATICADO)[number];

// ---------- Rascunho ----------

/** `preco` na condicao base = preco base; em outra condicao: `manual: true` + preco = override; `preco: null` = voltar a derivar da base. */
export const rascunhoItemSchema = z.object({
  itemId: cuid,
  tabelaId: cuid,
  condicaoId: cuid,
  preco: precoPositivo.nullable(),
  manual: z.boolean().default(false),
});
export type RascunhoItemDto = z.infer<typeof rascunhoItemSchema>;
export const gravarRascunhoSchema = z.object({ itens: z.array(rascunhoItemSchema).min(1).max(500) });
export type GravarRascunhoDto = z.infer<typeof gravarRascunhoSchema>;

export const descartarRascunhoSchema = z.object({ itemId: cuid.optional(), tabelaId: cuid.optional() });
export type DescartarRascunhoDto = z.infer<typeof descartarRascunhoSchema>;

export interface RascunhoLinhaResponse {
  itemId: string;
  itemCodigo: string;
  itemDescricao: string;
  tabelaId: string;
  tabelaCodigo: string;
  condicaoId: string;
  preco: string | null;
  manual: boolean;
  atualizadoEm: string;
}
export interface RascunhoResponse {
  linhas: RascunhoLinhaResponse[];
  /** Itens distintos e precos (linhas) no rascunho. */
  totalItens: number;
  totalPrecos: number;
}

// ---------- Visao do item (Formacao: um item, todas as tabelas) ----------

export interface LiberacaoCondicaoResponse {
  condicaoId: string;
  condicaoNome: string;
  base: boolean;
  /** Preco publicado vigente (null = nunca publicado). */
  publicado: string | null;
  publicadoOrigem: OrigemPraticado | null;
  /** Preco do rascunho NESTA condicao (base: o digitado; outra: o override manual). */
  rascunho: string | null;
  /** O que passa a valer se publicar agora: rascunho manual, ou derivado da base (rascunho ou publicada), ou o publicado. */
  efetivo: string | null;
  efetivoOrigem: OrigemPraticado | null;
  /** Lucro simulado do preco efetivo (fracao) e a faixa (green/orange/red), pelo motor. */
  lucro: string | null;
  faixa: 'green' | 'orange' | 'red' | null;
  sugerido: string;
}
export interface LiberacaoTabelaResponse {
  tabelaId: string;
  tabelaCodigo: string;
  tabelaNome: string;
  condicoes: LiberacaoCondicaoResponse[];
  lucroDesejado: string | null;
  lucroMinimo: string;
  /** Preco nao calculavel (item sem custo, sem tributos...): motivo. */
  indisponivel?: { code: string; message: string };
}
export interface LiberacaoItemResponse {
  itemId: string;
  itemCodigo: string;
  itemDescricao: string;
  tabelas: LiberacaoTabelaResponse[];
}

// ---------- Acoes em lote (sempre no rascunho) ----------

export const REGRAS_ARREDONDAMENTO = ['CINCO', 'DEZ', 'TERMINAR_90'] as const;
export const acaoLoteSchema = z.discriminatedUnion('tipo', [
  /** Preco base que da o lucro desejado informado em cada tabela. */
  z.object({ tipo: z.literal('LUCRO'), lucro: taxa }),
  /** O mesmo preco (com IPI, a prazo, na condicao base) em todas as tabelas. */
  z.object({ tipo: z.literal('MESMO_PRECO'), preco: precoPositivo }),
  /** Reajuste sobre o praticado (rascunho, ou o publicado se nao ha rascunho); pct em fracao (0.05 = +5%), pode ser negativo. */
  z.object({ tipo: z.literal('REAJUSTE'), pct: z.string().regex(/^-?\d{1,4}(\.\d{1,8})?$/, 'Percentual invalido (fracao, ate 8 casas)') }),
  z.object({ tipo: z.literal('ARREDONDAR'), regra: z.enum(REGRAS_ARREDONDAMENTO) }),
  z.object({ tipo: z.literal('COPIAR_SUGERIDO') }),
]);
export type AcaoLoteDto = z.infer<typeof acaoLoteSchema>;

export const escopoLoteSchema = z.object({
  /** Itens escolhidos (Formacao: 1 item). Vazio/ausente = todos os do filtro. */
  itemIds: z.array(cuid).max(500).optional(),
  /** Tabelas (ausente = todas as ativas com vigencia hoje). */
  tabelaIds: z.array(cuid).max(50).optional(),
  filtro: z
    .object({
      q: z.string().trim().max(100).optional(),
      tipo: itemTipo.optional(),
      semPraticado: z.boolean().optional(),
      semFicha: z.boolean().optional(),
    })
    .optional(),
});
export type EscopoLoteDto = z.infer<typeof escopoLoteSchema>;

/** PADRAO: grava a condicao padrao e as demais derivam. TODAS: calcula e grava (manual) o valor de cada condicao. */
export const APLICAR_EM_LOTE = ['PADRAO', 'TODAS'] as const;
export type AplicarEmLote = (typeof APLICAR_EM_LOTE)[number];

export const loteRascunhoSchema = z.object({ escopo: escopoLoteSchema, acao: acaoLoteSchema, aplicar: z.boolean().default(false), aplicarEm: z.enum(APLICAR_EM_LOTE).default('PADRAO') });
export type LoteRascunhoDto = z.infer<typeof loteRascunhoSchema>;

export interface PreviaLoteResponse {
  aplicado: boolean;
  /** Itens e precos (item x tabela) que a acao altera. */
  itens: number;
  precos: number;
  /** Pares sem como calcular (sem custo, sem tributo, sem base para reajustar/arredondar...). */
  ignorados: number;
  ignoradosMotivos: { code: string; quantidade: number }[];
  /** Menor e maior lucro simulado entre os precos resultantes (fracao). Nulos se nenhum preco. */
  menorLucro: string | null;
  maiorLucro: string | null;
  /** Quantos ficam abaixo do lucro minimo da tabela. */
  abaixoDoMinimo: number;
  /** Resultado por tabela e condicao (inclui as condicoes derivadas contadas na previa de LUCRO). */
  porCondicao: { tabela: string; condicao: string; precos: number; menorLucro: string; maiorLucro: string; abaixoDoMinimo: number }[];
}

// ---------- Publicacao ----------

export const publicarSchema = z
  .object({
    /** Inicio da vigencia (obrigatorio na tela; o padrao da API e hoje). Nao pode ser anterior a hoje; no futuro = publicacao programada. */
    validFrom: isoDate.optional(),
    /** Ultimo dia da vigencia (inclusive). Ausente = indeterminada. Nao pode ser anterior ao inicio. */
    validAte: isoDate.optional(),
    observacao: z.string().trim().max(300).optional(),
  })
  .refine((v) => !v.validFrom || !v.validAte || v.validAte >= v.validFrom, { message: 'O fim da vigência não pode ser anterior ao início', path: ['validAte'] });
export type PublicarDto = z.infer<typeof publicarSchema>;

export type SituacaoPublicacao = 'PROGRAMADA' | 'VIGENTE' | 'ENCERRADA';
export const ROTULO_SITUACAO_PUBLICACAO: Record<SituacaoPublicacao, string> = { PROGRAMADA: 'Programada', VIGENTE: 'Vigente', ENCERRADA: 'Encerrada' };

export interface PublicacaoResponse {
  id: string;
  validFrom: string;
  /** Ultimo dia (inclusive); null = indeterminada. */
  validAte: string | null;
  situacao: SituacaoPublicacao;
  publicadaEm: string;
  publicadoPorNome: string | null;
  observacao: string | null;
  totalItens: number;
  totalPrecos: number;
}

/** GET /precificacao/publicacoes?de=&ate=: so as que tocam o intervalo (calendario). */
export const listarPublicacoesSchema = z.object({ de: isoDate.optional(), ate: isoDate.optional() });
export type ListarPublicacoesQuery = z.infer<typeof listarPublicacoesSchema>;

/** Snapshot completo da publicacao (versao 1). Custo, sugerido, lucro e parametros so com custo.ver. */
export interface PublicacaoSnapshotPreco {
  itemId: string;
  itemCodigo: string;
  itemDescricao: string;
  tabelaId: string;
  tabelaCodigo: string;
  condicaoId: string;
  condicaoNome: string;
  preco: string;
  origem: OrigemPraticado;
  custo?: string;
  sugerido?: string;
  lucro?: string | null;
}
export interface PublicacaoSnapshot {
  versao: 1;
  publicadoPorNome: string | null;
  publicadaEm: string;
  validFrom: string;
  validAte: string | null;
  observacao: string | null;
  /** Linhas gravadas por esta publicacao (as que mudaram, ou que seguem valendo depois de uma janela que acaba). */
  precos: PublicacaoSnapshotPreco[];
  /** Estado vigente depois da publicacao em TODAS as condicoes dos itens publicados (base da comparacao). Publicacoes antigas nao tem. */
  vigentes?: PublicacaoSnapshotPreco[];
  /** Escopo na hora (ate a Sessao 35): itens que NAO eram liberados em tudo, com onde eram liberados. Publicacoes novas gravam `[]` e usam `naoLiberados`. */
  liberados: Array<{ itemId: string; itemCodigo: string; onde: Array<{ tabelaId: string; tabelaCodigo: string; condicaoId: string; condicaoNome: string }> }>;
  /** Sessao 36: excecoes "Nao liberar mesmo com preco" na hora da publicacao (por item). Publicacoes antigas nao tem. */
  naoLiberados?: Array<{ itemId: string; itemCodigo: string; onde: Array<{ tabelaId: string; tabelaCodigo: string; condicaoId: string; condicaoNome: string }> }>;
  parametros?: Array<{ tabelaId: string; tabelaCodigo: string; lucroDesejado: string | null; lucroMinimo: string; despesaFixa: string; custoFinanceiro: string }>;
  /** S30-0b: preços gravados sem custo/sugerido/lucro porque o motor não calculou (a publicação segue). Publicações antigas não têm. */
  semCusto?: PublicacaoSnapshotFalha[];
}
export interface PublicacaoSnapshotFalha {
  itemId: string;
  itemCodigo: string;
  itemDescricao: string;
  tabelaCodigo: string;
  condicaoNome: string;
  codigo: string;
  /** Some sem custo.ver. */
  mensagem?: string;
}
export interface PublicacaoDetalheResponse extends PublicacaoResponse {
  snapshot: PublicacaoSnapshot | null;
}

/** GET /precificacao/publicacoes/comparar?a=&b= */
export const compararPublicacoesSchema = z.object({ a: cuid, b: cuid });
export type CompararPublicacoesQuery = z.infer<typeof compararPublicacoesSchema>;
export interface ComparacaoPublicacaoLinha {
  itemCodigo: string;
  itemDescricao: string;
  tabelaCodigo: string;
  condicaoNome: string;
  precoA: string | null;
  precoB: string | null;
  /** B - A (so quando os dois existem). */
  diferenca: string | null;
  /** (B - A) / A em fracao, 4 casas (so quando os dois existem e A > 0). */
  variacao: string | null;
}
export interface ComparacaoPublicacoesResponse {
  a: PublicacaoResponse;
  b: PublicacaoResponse;
  linhas: ComparacaoPublicacaoLinha[];
}

/**
 * Excecoes do item (Sessao 36): GET/PUT /precificacao/liberacao/escopo/:id. A liberacao e automatica (preco praticado vigente = liberado);
 * as combinacoes aqui ficam NAO liberadas mesmo com preco ("Nao liberar mesmo com preco", Acoes em lote). Lista vazia no PUT = sem excecoes.
 */
export const escopoItemSchema = z.object({ naoLiberados: z.array(z.object({ tabelaId: cuid, condicaoId: cuid })).max(500) });
export type EscopoItemDto = z.infer<typeof escopoItemSchema>;
export interface EscopoItemResponse {
  itemId: string;
  naoLiberados: Array<{ tabelaId: string; tabelaCodigo: string; condicaoId: string; condicaoNome: string }>;
}

export interface PublicarResponse extends PublicacaoResponse {
  /** Marcas "revisar preco" resolvidas pelos novos precos. */
  marcasResolvidas: number;
}
